// ============================================================================
//  Motor de SELECCIONES (clasificatorias + Mundial) — corre client-side
// ============================================================================
//
// Patrón: igual que worldSim.js — PRNG determinístico sembrado con (seed, ciclo)
// para que el sorteo de grupos y los partidos de fondo sean reproducibles. Los
// partidos del JUGADOR usan `simulateMatch` (Math.random) porque tienen modal
// vivo y queremos varianza.
//
// API pública que consume Stage 3 (careerTurn integration):
//
//   iniciarCicloClasif(seleccion, seed, ciclo, edad_jugador, totalJornadasLiga)
//      → state inicial: grupo del jugador, calendario anual del jugador,
//        standings de todos los grupos en 0.
//
//   simularPartidoFondo(home, away, rng) → {gh, ga}   // sin jugador, determinístico
//
//   procesarFinDeAño(state, año)
//      → simula los partidos del grupo del jugador (los que NO juega él) hasta
//        cerrar el año. Actualiza standings. Si es año 3 (último de clasif),
//        decide estado: 'clasificado', 'playoff' o 'eliminado'.
//
//   iniciarPlayoff(state, seed)
//      → si el país quedó en playoff, arma las llaves (2 rondas a un partido).
//        Devuelve nuevos partidos pendientes del jugador.
//
//   sortearMundial(clasificados, seed, ciclo)
//      → 12 grupos × 4. Determinístico.
//
//   iniciarKOMundial(mundial)
//      → de la fase de grupos al bracket de 16avos (12 primeros + 12 segundos
//        + 8 mejores terceros).
//
//   avanzarRondaKO(mundial, resultados)
//      → de cuartos a semis, semis a final, etc.

import {
    SELECCIONES_BY_CONFEDERACION, SELECCIONES_BY_PAIS, MUNDIAL_CUPOS,
} from './selecciones';

// ── PRNG mulberry32 + hash determinístico (copiado de worldSim) ─────────────

function makeRng(seed) {
    let a = seed >>> 0;
    return function () {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hashSeed(...parts) {
    const str = parts.join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

function poisson(lambda, rng) {
    const L = Math.exp(-lambda);
    let k = 0, p = 1;
    do { k++; p *= rng(); } while (p > L);
    return k - 1;
}

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

// Shuffle determinístico (Fisher-Yates con PRNG inyectado).
function shuffleDet(arr, rng) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// Simulación de partido de FONDO (sin jugador): mismo modelo que worldSim pero
// con estrellas de selección. Determinístico con el rng inyectado.
function simBackgroundMatch(home, away, rng) {
    const diff = (home.estrellas || 1) - (away.estrellas || 1);
    let lh = (1.3 + diff * 0.22 + 0.2) * (0.74 + rng() * 0.52); // +0.2 = ventaja local
    let la = (1.1 - diff * 0.22) * (0.74 + rng() * 0.52);
    lh = clamp(lh, 0.2, 4.5);
    la = clamp(la, 0.2, 4.5);
    return { gh: poisson(lh, rng), ga: poisson(la, rng) };
}

// Prórroga (2x15, 30') de un cruce de FONDO que llegó empatado a los 90'. Mismo modelo que
// simBackgroundMatch escalado a 1/3 de partido.
function simExtraTimeBackground(home, away, rng) {
    const diff = (home.estrellas || 1) - (away.estrellas || 1);
    const factorTiempo = 30 / 90;
    let lh = (1.3 + diff * 0.22 + 0.2) * factorTiempo * (0.74 + rng() * 0.52);
    let la = (1.1 - diff * 0.22) * factorTiempo * (0.74 + rng() * 0.52);
    lh = clamp(lh, 0.05, 2);
    la = clamp(la, 0.05, 2);
    return { gh: poisson(lh, rng), ga: poisson(la, rng) };
}

// Tanda de penales de FONDO (determinística vía rng inyectado), mismo modelo que
// simulatePenaltyShootout de matchEngine.js pero sin Math.random().
function simPenalesBackground(estrellasA, estrellasB, rng) {
    const pA = clamp(0.72 + (estrellasA - estrellasB) * 0.03, 0.55, 0.9);
    const pB = clamp(0.72 + (estrellasB - estrellasA) * 0.03, 0.55, 0.9);
    let a = 0, b = 0, ka = 0, kb = 0;
    const N = 5;
    const decidido = () => (a > b + (N - kb)) || (b > a + (N - ka));
    while ((ka < N || kb < N) && !decidido()) {
        if (ka <= kb && ka < N) { if (rng() < pA) a++; ka++; }
        else if (kb < N) { if (rng() < pB) b++; kb++; }
        else break;
    }
    let guard = 0;
    while (a === b && guard++ < 50) {
        if (rng() < pA) a++;
        if (rng() < pB) b++;
    }
    return a > b ? 'a' : 'b';
}

// ── Sorteo de grupos por confederación ──────────────────────────────────────

function bombosDeConf(confederacion) {
    const todas = SELECCIONES_BY_CONFEDERACION[confederacion] || [];
    const bombos = {};
    for (const s of todas) {
        if (s.bombo != null) (bombos[s.bombo] = bombos[s.bombo] || []).push(s);
    }
    return bombos;
}

// UEFA: 12 grupos. 6 grupos de 4 (B1+B2+B3+B4) y 6 grupos de 5 (con B5).
// B5 tiene solo 6 países → solo 6 grupos reciben uno.
function sortearGruposUEFA(seed, ciclo) {
    const rng = makeRng(hashSeed(seed, ciclo, 'UEFA'));
    const b = bombosDeConf('UEFA');
    const b1 = shuffleDet(b[1], rng);  // 12
    const b2 = shuffleDet(b[2], rng);  // 12
    const b3 = shuffleDet(b[3], rng);  // 12
    const b4 = shuffleDet(b[4], rng);  // 12
    const b5 = shuffleDet(b[5], rng);  // 6
    const grupos = [];
    for (let i = 0; i < 12; i++) {
        const equipos = [b1[i], b2[i], b3[i], b4[i]];
        if (i < 6) equipos.push(b5[i]); // los primeros 6 grupos tienen 5 equipos
        grupos.push({ indice: i, equipos });
    }
    return grupos;
}

// Asia: 4 grupos. 2 B1 + 2 B2 + 2 B3 + (5 o 6) B4. B4 tiene 22 → 5+5+6+6.
function sortearGruposAsia(seed, ciclo) {
    const rng = makeRng(hashSeed(seed, ciclo, 'Asia'));
    const b = bombosDeConf('Asia');
    const b1 = shuffleDet(b[1], rng); // 8
    const b2 = shuffleDet(b[2], rng); // 8
    const b3 = shuffleDet(b[3], rng); // 8
    const b4 = shuffleDet(b[4], rng); // 22
    const grupos = [];
    // Repartir B4: grupos 0 y 1 con 5, grupos 2 y 3 con 6. Total 5+5+6+6 = 22.
    const b4Splits = [5, 5, 6, 6];
    let b4Pos = 0;
    for (let i = 0; i < 4; i++) {
        const equipos = [
            b1[i * 2], b1[i * 2 + 1],
            b2[i * 2], b2[i * 2 + 1],
            b3[i * 2], b3[i * 2 + 1],
            ...b4.slice(b4Pos, b4Pos + b4Splits[i]),
        ];
        b4Pos += b4Splits[i];
        grupos.push({ indice: i, equipos });
    }
    return grupos;
}

// África: el catálogo tiene 54 (no 53 de la spec FIFA real). Para que cuadre
// hago 9 grupos de 6 (54 exactos), tomando 1 de cada bombo (1..6) por grupo.
function sortearGruposAfrica(seed, ciclo) {
    const rng = makeRng(hashSeed(seed, ciclo, 'Africa'));
    const b = bombosDeConf('Africa');
    const bombos = [1, 2, 3, 4, 5, 6].map(n => shuffleDet(b[n] || [], rng));
    const grupos = [];
    for (let i = 0; i < 9; i++) {
        const equipos = bombos.map(arr => arr[i]).filter(Boolean);
        grupos.push({ indice: i, equipos });
    }
    return grupos;
}

// CONCACAF: B4 tiene 16 países → playoff previo (2 rondas a 1 partido, 16→8→4).
// El motor SIMPLIFICA: tomamos los 4 mejor rankeados de B4 (deterministic) y
// pasan directamente a la fase de grupos. Los otros 12 quedan eliminados.
// Después 4 grupos de 4: 1 de cada bombo (B1+B2+B3+B4_top4).
function sortearGruposCONCACAF(seed, ciclo) {
    const rng = makeRng(hashSeed(seed, ciclo, 'CONCACAF'));
    const b = bombosDeConf('CONCACAF');
    const b1 = shuffleDet(b[1], rng); // 4
    const b2 = shuffleDet(b[2], rng); // 4
    const b3 = shuffleDet(b[3], rng); // 4
    // B4: los 4 mejores por ranking (preclasif simplificada).
    const b4Top4 = [...b[4]].sort((x, y) => x.ranking - y.ranking).slice(0, 4);
    const b4 = shuffleDet(b4Top4, rng);
    const grupos = [];
    for (let i = 0; i < 4; i++) {
        grupos.push({ indice: i, equipos: [b1[i], b2[i], b3[i], b4[i]] });
    }
    return grupos;
}

// Conmebol: liga única, ida y vuelta. "1 grupo" con los 10 equipos.
function sortearGruposConmebol(/* seed, ciclo */) {
    return [{ indice: 0, equipos: [...SELECCIONES_BY_CONFEDERACION.Conmebol] }];
}

// Oceanía: liga única, ida y vuelta. "1 grupo" con los 11 equipos.
function sortearGruposOceania(/* seed, ciclo */) {
    return [{ indice: 0, equipos: [...SELECCIONES_BY_CONFEDERACION.Oceania] }];
}

function sortearGrupos(confederacion, seed, ciclo) {
    switch (confederacion) {
        case 'UEFA':     return sortearGruposUEFA(seed, ciclo);
        case 'Asia':     return sortearGruposAsia(seed, ciclo);
        case 'Africa':   return sortearGruposAfrica(seed, ciclo);
        case 'CONCACAF': return sortearGruposCONCACAF(seed, ciclo);
        case 'Conmebol': return sortearGruposConmebol(seed, ciclo);
        case 'Oceania':  return sortearGruposOceania(seed, ciclo);
        default: return [];
    }
}

// ── Round-robin ida y vuelta + distribución en 3 años ───────────────────────

// Método del círculo (Berger): genera la 1ª vuelta (n-1 rondas). Después espejo
// para la 2ª vuelta (intercambia local/visita). Determinístico dado un orden inicial.
function roundRobinIdaVuelta(equipos) {
    const arr = [...equipos];
    if (arr.length % 2 !== 0) arr.push(null); // bye
    const n = arr.length, half = n / 2, rondas = n - 1;
    let rot = [...arr];
    const ida = [];
    for (let r = 0; r < rondas; r++) {
        const partidos = [];
        for (let i = 0; i < half; i++) {
            const a = rot[i], b = rot[n - 1 - i];
            if (a == null || b == null) continue;
            // Alterna localía para que cada equipo no juegue siempre local en su 1ª vuelta.
            partidos.push(r % 2 === 0 ? [a, b] : [b, a]);
        }
        ida.push(partidos);
        const fixed = rot[0];
        const rest = rot.slice(1);
        rest.unshift(rest.pop());
        rot = [fixed, ...rest];
    }
    const vuelta = ida.map(ronda => ronda.map(([a, b]) => [b, a]));
    return [...ida, ...vuelta]; // array de rondas, cada ronda = array de [home, away]
}

// Distribuye las rondas en 3 años. Reparte equitativamente: las primeras rondas
// son año 1, las del medio año 2, las últimas año 3.
function distribuirRondasEnAños(rondas, años = 3) {
    const total = rondas.length;
    const porAño = Math.ceil(total / años);
    return rondas.map((ronda, idx) => ({
        ronda: idx + 1,
        año: Math.min(años, Math.floor(idx / porAño) + 1),
        partidos: ronda,
    }));
}

// Calendario completo del GRUPO (todos los partidos) con año asignado.
function generarCalendarioGrupo(equipos) {
    const rondas = roundRobinIdaVuelta(equipos);
    return distribuirRondasEnAños(rondas);
}

// Saca solo los partidos del jugador (los que involucran a su país), en orden.
function calendarioJugadorEnGrupo(calendarioGrupo, paisJugador) {
    const partidos = [];
    for (const ronda of calendarioGrupo) {
        for (const [home, away] of ronda.partidos) {
            const homeP = home.pais || home;
            const awayP = away.pais || away;
            if (homeP === paisJugador || awayP === paisJugador) {
                partidos.push({
                    ronda: ronda.ronda,
                    año: ronda.año,
                    home,
                    away,
                    local: homeP === paisJugador,
                    rival: homeP === paisJugador ? away : home,
                    jugado: false,
                    gh: null,
                    ga: null,
                });
            }
        }
    }
    return partidos;
}

// ── Standings + ordenamiento ────────────────────────────────────────────────

function emptyStandings(equipos) {
    const s = {};
    for (const e of equipos) {
        s[e.pais] = { pais: e.pais, nombre: e.nombre, estrellas: e.estrellas, ranking: e.ranking, pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, pts: 0 };
    }
    return s;
}

function aplicarResultadoStandings(standings, home, away, gh, ga) {
    const h = standings[home.pais || home];
    const a = standings[away.pais || away];
    if (!h || !a) return;
    h.pj++; a.pj++;
    h.gf += gh; h.gc += ga;
    a.gf += ga; a.gc += gh;
    if (gh > ga) { h.pg++; h.pts += 3; a.pp++; }
    else if (gh < ga) { a.pg++; a.pts += 3; h.pp++; }
    else { h.pe++; a.pe++; h.pts++; a.pts++; }
}

function ordenarStandings(standings) {
    return Object.values(standings).sort((a, b) =>
        (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf) || String(a.pais).localeCompare(String(b.pais)));
}

// ── Simulación de fondo (no-jugador) ────────────────────────────────────────

// Simula todos los partidos del grupo EXCEPTO los del jugador (ese los juega
// en vivo). Determinístico con seed+ciclo+grupoIdx. Actualiza el `standings`
// in-place. Solo procesa partidos no jugados todavía (idempotente por partido).
function simularPartidosFondoDeGrupo(grupo, standings, calendarioGrupo, paisJugador, seed, ciclo, hastaAño, hastaRonda = Infinity) {
    const rng = makeRng(hashSeed(seed, ciclo, 'fondo', grupo.indice));
    for (const ronda of calendarioGrupo) {
        if (ronda.año > hastaAño || ronda.ronda > hastaRonda) break;
        for (const [home, away] of ronda.partidos) {
            const homeP = home.pais || home;
            const awayP = away.pais || away;
            // Saltamos los partidos del jugador (los juega él) — pero igual avanzamos
            // el rng para no romper la determinismo si el orden cambia.
            const esDelJugador = homeP === paisJugador || awayP === paisJugador;
            const { gh, ga } = simBackgroundMatch(home, away, rng);
            if (!esDelJugador) {
                aplicarResultadoStandings(standings, home, away, gh, ga);
            }
        }
    }
}

// ── API pública ─────────────────────────────────────────────────────────────

// Inicia un nuevo ciclo de clasificatorias. Sortea grupos y arma el calendario.
// Pre-repechaje CONCACAF: los 16 del bombo 4 juegan un playoff a partido único hasta quedar 4
// que entran a la fase de grupos. Para el jugador (si es del bombo 4) construimos su camino: 2
// rondas a partido único (16→8→4). Gana las dos → entra a los grupos; pierde alguna → eliminado.
function construirPrePlayoffConcacaf(seleccionJugador, seed, ciclo) {
    const rng = makeRng(hashSeed(seed, ciclo, 'CONCACAF', 'preplayoff'));
    const bombo4 = (SELECCIONES_BY_CONFEDERACION.CONCACAF || []).filter(s => s.bombo === 4 && s.pais !== seleccionJugador.pais);
    const otros = shuffleDet(bombo4, rng);
    const r1 = otros[0], r2 = otros[1] || otros[0];
    return {
        ties: [
            { ronda: 'Pre-repechaje · Ronda 1', rival_pais: r1.pais, rival_nombre: r1.nombre, leg: 'single' },
            { ronda: 'Pre-repechaje · Ronda 2', rival_pais: r2.pais, rival_nombre: r2.nombre, leg: 'single' },
        ],
        idx: 0, fase: 'jugando',
    };
}

// Devuelve el estado completo del ciclo para guardar en `career.seleccion`.
function iniciarCicloClasif(seleccionJugador, seed, ciclo) {
    if (!seleccionJugador) return null;
    const conf = seleccionJugador.confederacion;
    const grupos = sortearGrupos(conf, seed, ciclo).map(g => ({
        ...g,
        calendarioCompleto: generarCalendarioGrupo(g.equipos),
        standings: emptyStandings(g.equipos),
    }));

    // Encontrar el grupo del jugador.
    let grupoJugadorIdx = grupos.findIndex(g => g.equipos.some(e => e.pais === seleccionJugador.pais));

    // CONCACAF bombo 4: SIEMPRE juega el pre-repechaje. Si no entró al sorteo de grupos (no era
    // top-4 del bombo 4), lo metemos a la fuerza reemplazando al bombo 4 de peor ranking (asumiendo
    // que va a ganar el pre-repechaje; si lo pierde, queda eliminado igual).
    let preplayoff = null;
    if (conf === 'CONCACAF' && seleccionJugador.bombo === 4) {
        if (grupoJugadorIdx < 0) {
            let worst = null;
            for (const g of grupos) {
                for (let i = 0; i < g.equipos.length; i++) {
                    const e = g.equipos[i];
                    if (e && e.bombo === 4 && (!worst || e.ranking > worst.e.ranking)) worst = { g, i, e };
                }
            }
            if (worst) {
                worst.g.equipos[worst.i] = seleccionJugador;
                worst.g.calendarioCompleto = generarCalendarioGrupo(worst.g.equipos);
                worst.g.standings = emptyStandings(worst.g.equipos);
                grupoJugadorIdx = worst.g.indice;
            }
        }
        preplayoff = construirPrePlayoffConcacaf(seleccionJugador, seed, ciclo);
    }

    if (grupoJugadorIdx < 0) {
        return {
            pais: seleccionJugador.pais,
            confederacion: conf,
            ciclo,
            grupos,
            grupoJugadorIdx: -1,
            calendarioJugador: [],
            fase: 'eliminado',
            playoff: null,
            stats_ciclo: { partidos: 0, goles: 0, asistencias: 0 },
            eliminado_motivo: 'No clasificó al sorteo de grupos.',
        };
    }
    const grupoJugador = grupos[grupoJugadorIdx];
    const calendarioJugador = calendarioJugadorEnGrupo(grupoJugador.calendarioCompleto, seleccionJugador.pais);

    return {
        pais: seleccionJugador.pais,
        confederacion: conf,
        ciclo,
        grupos,
        grupoJugadorIdx,
        calendarioJugador,
        fase: preplayoff ? 'preplayoff' : 'clasif', // 'preplayoff' | 'clasif' | 'playoff' | 'clasificado' | 'eliminado'
        preplayoff,
        preplayoff_turno_jornada: -1,
        playoff: null,
        stats_ciclo: { partidos: 0, goles: 0, asistencias: 0 },
    };
}

// Refresca la tabla del grupo del jugador DURANTE la clasificatoria para que los demás países
// también jueguen (no solo el jugador). Recalcula = partidos jugados del jugador + simulación de
// fondo de los demás, hasta la ronda más avanzada que jugó el jugador. Determinístico.
// De paso, refresca también los demás grupos (no tienen partido del jugador, así que se simulan
// enteros hasta esa misma ronda) para que las tablas alternativas (segundos, terceros de cada
// grupo) muestren datos reales en vez de quedar en 0 hasta el cierre del año.
function refrescarStandingsGrupoJugador(state, seed) {
    if (state.grupoJugadorIdx < 0) return;
    const grupo = state.grupos[state.grupoJugadorIdx];
    if (!grupo) return;
    const jugados = state.calendarioJugador.filter(p => p.jugado);
    const maxRonda = jugados.reduce((m, p) => Math.max(m, p.ronda || 0), 0);
    grupo.standings = emptyStandings(grupo.equipos);
    for (const p of jugados) {
        if (p.año <= state.año_ciclo) aplicarResultadoStandings(grupo.standings, p.home, p.away, p.gh, p.ga);
    }
    simularPartidosFondoDeGrupo(grupo, grupo.standings, grupo.calendarioCompleto, state.pais, seed, state.ciclo, state.año_ciclo, maxRonda);

    for (const otro of state.grupos) {
        if (otro.indice === state.grupoJugadorIdx) continue;
        otro.standings = emptyStandings(otro.equipos);
        simularPartidosFondoDeGrupo(otro, otro.standings, otro.calendarioCompleto, '___NO_HAY___', seed, state.ciclo, state.año_ciclo, maxRonda);
    }
}

// Marca un partido del jugador como jugado (después de simularlo en vivo).
// Actualiza standings del grupo + stats del ciclo.
function aplicarPartidoJugador(state, partidoIndex, resultado) {
    const partido = state.calendarioJugador[partidoIndex];
    if (!partido || partido.jugado) return state;
    partido.jugado = true;
    partido.gh = resultado.gh;
    partido.ga = resultado.ga;
    partido.minutos = resultado.minutos_jugados;
    partido.goles_jugador = resultado.goles_jugador;
    partido.asistencias_jugador = resultado.asistencias_jugador;
    partido.amarilla = resultado.amarilla;
    partido.roja = resultado.roja;
    partido.no_convocado = !!resultado.no_convocado;

    const grupo = state.grupos[state.grupoJugadorIdx];
    aplicarResultadoStandings(grupo.standings, partido.home, partido.away, resultado.gh, resultado.ga);
    if (!resultado.no_convocado) {
        state.stats_ciclo.partidos += 1;
        state.stats_ciclo.goles += (resultado.goles_jugador || 0);
        state.stats_ciclo.asistencias += (resultado.asistencias_jugador || 0);
    }
    return state;
}

// Simula los partidos de fondo (los que NO juega el jugador) en TODOS los grupos
// hasta cerrar `hastaAño`. Actualiza standings. Idempotente: solo procesa lo no
// procesado todavía gracias al seed determinístico + filtro por año.
function procesarFinDeAño(state, hastaAño, seed) {
    for (const grupo of state.grupos) {
        // El grupo del jugador: simular fondo (no-jugador). Otros grupos: simular TODO.
        const paisJ = grupo.indice === state.grupoJugadorIdx ? state.pais : '___NO_HAY___';
        // Reset y resimulación completa de standings hasta hastaAño para ser idempotente.
        grupo.standings = emptyStandings(grupo.equipos);
        if (grupo.indice === state.grupoJugadorIdx) {
            // Aplicar primero los resultados del jugador ya jugados.
            for (const p of state.calendarioJugador) {
                if (p.jugado && p.año <= hastaAño) {
                    aplicarResultadoStandings(grupo.standings, p.home, p.away, p.gh, p.ga);
                }
            }
        }
        simularPartidosFondoDeGrupo(grupo, grupo.standings, grupo.calendarioCompleto, paisJ, seed, state.ciclo, hastaAño);
    }
    return state;
}

// Determina la clasificación final del PAÍS del jugador después del año 3.
// Devuelve nueva `fase`: 'clasificado' | 'playoff' | 'eliminado'.
function determinarClasificacion(state) {
    const conf = state.confederacion;
    const grupoJ = state.grupos[state.grupoJugadorIdx];
    if (!grupoJ) { state.fase = 'eliminado'; return state; }
    const tabla = ordenarStandings(grupoJ.standings);
    const posicion = tabla.findIndex(e => e.pais === state.pais) + 1;
    state.posicion_grupo = posicion;

    // Reglas por confederación según spec del feature:
    if (conf === 'UEFA') {
        // 1º clasifica directo. 2º + 4 mejores terceros van a playoff. Resto eliminado.
        if (posicion === 1) state.fase = 'clasificado';
        else if (posicion === 2) state.fase = 'playoff';
        else if (posicion === 3 && esMejorTercero(state, 4)) state.fase = 'playoff';
        else state.fase = 'eliminado';
    } else if (conf === 'Asia') {
        // 1º y 2º clasifican directo. 3os juegan playoff (1 cupo).
        if (posicion <= 2) state.fase = 'clasificado';
        else if (posicion === 3) state.fase = 'playoff';
        else state.fase = 'eliminado';
    } else if (conf === 'Africa') {
        // 1º clasifica directo. 4 mejores 2º juegan playoff (1 cupo).
        if (posicion === 1) state.fase = 'clasificado';
        else if (posicion === 2 && esMejorSegundo(state, 4)) state.fase = 'playoff';
        else state.fase = 'eliminado';
    } else if (conf === 'CONCACAF') {
        // 1º clasifica directo. 2º juega final cruzada por 1 cupo.
        if (posicion === 1) state.fase = 'clasificado';
        else if (posicion === 2) state.fase = 'playoff';
        else state.fase = 'eliminado';
    } else if (conf === 'Conmebol') {
        // Primeros 6 clasifican directo. 7º, 8º, 9º, 10º eliminados.
        if (posicion <= 6) state.fase = 'clasificado';
        else state.fase = 'eliminado';
    } else if (conf === 'Oceania') {
        // 1º clasifica directo (1 cupo según ajuste).
        if (posicion === 1) state.fase = 'clasificado';
        else state.fase = 'eliminado';
    }
    return state;
}

// ¿Mi país está entre los N mejores terceros de TODOS los grupos de su confederación?
function esMejorTercero(state, N) {
    const terceros = state.grupos
        .map(g => ({ tabla: ordenarStandings(g.standings) }))
        .map(({ tabla }) => tabla[2])
        .filter(Boolean)
        .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf));
    return terceros.slice(0, N).some(t => t.pais === state.pais);
}

function esMejorSegundo(state, N) {
    const segundos = state.grupos
        .map(g => ({ tabla: ordenarStandings(g.standings) }))
        .map(({ tabla }) => tabla[1])
        .filter(Boolean)
        .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf));
    return segundos.slice(0, N).some(t => t.pais === state.pais);
}

// ── Lista de clasificados al Mundial por confederación (para sortear el mundial) ──
// Se simulan playoffs por confederación con seed+ciclo. Devuelve [...selecciones].
function clasificadosAlMundialConf(conf, seed, ciclo) {
    // Sortea, simula todo, devuelve los N clasificados según los cupos de MUNDIAL_CUPOS.
    const cupos = MUNDIAL_CUPOS[conf] || 0;
    const grupos = sortearGrupos(conf, seed, ciclo).map(g => ({
        ...g,
        calendarioCompleto: generarCalendarioGrupo(g.equipos),
        standings: emptyStandings(g.equipos),
    }));
    const rng = makeRng(hashSeed(seed, ciclo, 'todoFondo', conf));
    for (const g of grupos) {
        for (const ronda of g.calendarioCompleto) {
            for (const [home, away] of ronda.partidos) {
                const { gh, ga } = simBackgroundMatch(home, away, rng);
                aplicarResultadoStandings(g.standings, home, away, gh, ga);
            }
        }
    }
    const tablas = grupos.map(g => ordenarStandings(g.standings));
    return clasificadosDeTablas(conf, tablas, rng, cupos, null);
}

// Aplica las reglas de clasificación de una confederación a partir de sus tablas de grupos ya
// jugadas. `excluirPaises` (array de códigos de país) se saca de los pools de PLAYOFF (no de
// los clasificados directos) — se usa para la confederación del jugador, cuyo país se maneja
// aparte según su resultado real, Y para los rivales que el jugador eliminó de VERDAD en su
// propio repechaje (si no se excluyeran acá, esta simulación de fondo independiente podría
// hacerlos "ganar" y aparecer en el Mundial pese a estar eliminados — ver `po.eliminados`).
function clasificadosDeTablas(conf, tablas, rng, cupos, excluirPaises) {
    const excluidos = new Set((excluirPaises || []).filter(Boolean));
    const noEx = (arr) => arr.filter(s => s && !excluidos.has(s.pais));
    const clasificados = [];
    if (conf === 'UEFA') {
        for (const tabla of tablas) clasificados.push(byPais(tabla[0]));
        const segundos = tablas.map(t => t[1]).filter(Boolean);
        const terceros = tablas.map(t => t[2]).filter(Boolean)
            .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)));
        clasificados.push(...simularPlayoffSimple(noEx([...segundos, ...terceros.slice(0, 4)]), rng, 4));
    } else if (conf === 'Asia') {
        for (const tabla of tablas) { clasificados.push(byPais(tabla[0])); clasificados.push(byPais(tabla[1])); }
        const terceros = tablas.map(t => t[2]).filter(Boolean);
        clasificados.push(...simularPlayoffSimple(noEx(terceros), rng, 1));
    } else if (conf === 'Africa') {
        for (const tabla of tablas) clasificados.push(byPais(tabla[0])); // primeros directo
        const segundos = tablas.map(t => t[1]).filter(Boolean)
            .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)));
        clasificados.push(...simularPlayoffSimple(noEx(segundos.slice(0, 4)), rng, 1));
    } else if (conf === 'CONCACAF') {
        for (const tabla of tablas) clasificados.push(byPais(tabla[0])); // primeros directo
        const segundos = tablas.map(t => t[1]).filter(Boolean);
        clasificados.push(...simularCrucesCONCACAF(segundos, rng));
    } else if (conf === 'Conmebol' || conf === 'Oceania') {
        clasificados.push(...tablas[0].slice(0, cupos).map(byPais));
    }
    return clasificados.filter(Boolean).slice(0, cupos);
}

// Clasificados de la confederación del JUGADOR a partir de su estado real de clasificatorias
// (state.grupos ya simulado con SUS resultados), para que coincida con lo que el jugador vio
// (p. ej. el 1º de su grupo SÍ va al Mundial). El país del jugador se excluye del playoff y se
// agrega/quita aparte según haya clasificado o no. Además se excluyen los países que el jugador
// eliminó de verdad en SU repechaje (`state.playoff.eliminados`), para que esta simulación de
// fondo (independiente del repechaje que jugó el jugador) nunca los haga "reaparecer".
function clasificadosConfDesdeEstado(state, seed, ciclo) {
    const conf = state.confederacion;
    const cupos = MUNDIAL_CUPOS[conf] || 0;
    const tablas = (state.grupos || []).map(g => ordenarStandings(g.standings));
    const rng = makeRng(hashSeed(seed, ciclo, 'playoffDesdeEstado', conf));
    const excluidos = [state.pais, ...((state.playoff && state.playoff.eliminados) || [])];
    return clasificadosDeTablas(conf, tablas, rng, cupos, excluidos);
}

function byPais(standing) {
    if (!standing) return null;
    return SELECCIONES_BY_PAIS[standing.pais] || null;
}

// Playoff simple: emparejamientos 1vs último, 2vs anteúltimo, etc. A 1 partido. Cuantas
// rondas hagan falta para reducir a N ganadores. Determinístico.
function simularPlayoffSimple(equipos, rng, ganadoresFinales) {
    let vivos = [...equipos].sort((a, b) => (a.estrellas || 1) - (b.estrellas || 1));
    while (vivos.length > ganadoresFinales) {
        const next = [];
        const pares = Math.floor(vivos.length / 2);
        for (let i = 0; i < pares; i++) {
            const home = vivos[i];
            const away = vivos[vivos.length - 1 - i];
            const { gh, ga } = simBackgroundMatch(home, away, rng);
            next.push(gh >= ga ? home : away); // empate → favorece local
        }
        // Cantidad impar de equipos (p. ej. tras excluir eliminados de verdad por el jugador):
        // el del medio (sin rival) pasa de ronda directo en vez de "jugar contra sí mismo".
        if (vivos.length % 2 === 1) next.push(vivos[pares]);
        vivos = next;
    }
    return vivos.map(byPais);
}

// CONCACAF: 4 segundos cruzados G1-G3 y G2-G4, ida y vuelta. 2 ganadores.
function simularCrucesCONCACAF(segundos, rng) {
    if (segundos.length !== 4) return [];
    const cruces = [[segundos[0], segundos[2]], [segundos[1], segundos[3]]];
    const ganadores = [];
    for (const [a, b] of cruces) {
        const ida = simBackgroundMatch(a, b, rng);
        const vuelta = simBackgroundMatch(b, a, rng);
        const globalA = ida.gh + vuelta.ga;
        const globalB = ida.ga + vuelta.gh;
        ganadores.push(globalA >= globalB ? a : b);
    }
    return ganadores.map(byPais);
}

// ── Mundial: sorteo + bracket ───────────────────────────────────────────────

// Junta los 48 clasificados (algunos del país del jugador si clasificó) y arma 12 grupos.
// Bombos por ranking FIFA: 1-12 = B1, 13-24 = B2, 25-36 = B3, 37-48 = B4.
// Máximo de selecciones de una misma confederación por grupo del Mundial: 1 para todas, salvo
// Europa (UEFA) que admite hasta 2 (es la confederación con más cupos, igual que en la FIFA real).
function maxPorConfMundial(confederacion) {
    return confederacion === 'UEFA' ? 2 : 1;
}

function sortearMundial(clasificados, seed, ciclo) {
    if (clasificados.length !== 48) {
        // Si por algún ajuste manual no llegan a 48, completamos con dummies para no romper.
        // Esto no debería pasar con los cupos correctos.
    }
    const NG = 12;
    const ordenados = [...clasificados].sort((a, b) => a.ranking - b.ranking);
    const rng = makeRng(hashSeed(seed, ciclo, 'mundialSorteo'));
    // Bombos por ranking FIFA, mezclados determinísticamente.
    const pots = [
        shuffleDet(ordenados.slice(0, 12), rng),
        shuffleDet(ordenados.slice(12, 24), rng),
        shuffleDet(ordenados.slice(24, 36), rng),
        shuffleDet(ordenados.slice(36, 48), rng),
    ];

    // Sorteo con restricción de confederación: cada grupo recibe exactamente un equipo de cada
    // bombo y, por confederación, como mucho maxPorConfMundial() equipos. Se resuelve con
    // backtracking determinístico (recorriendo los grupos en orden de índice).
    const puedeIr = (grupo, equipo, potIdx) => {
        if (grupo.length !== potIdx) return false; // este grupo todavía no recibió un equipo de este bombo
        const c = equipo.confederacion;
        const yaDeEsaConf = grupo.reduce((n, e) => n + (e.confederacion === c ? 1 : 0), 0);
        return yaDeEsaConf < maxPorConfMundial(c);
    };
    const armar = () => {
        const grupos = Array.from({ length: NG }, () => []);
        const colocar = (potIdx, teamIdx) => {
            if (potIdx === pots.length) return true;
            if (teamIdx === pots[potIdx].length) return colocar(potIdx + 1, 0);
            const equipo = pots[potIdx][teamIdx];
            for (let g = 0; g < NG; g++) {
                if (!puedeIr(grupos[g], equipo, potIdx)) continue;
                grupos[g].push(equipo);
                if (colocar(potIdx, teamIdx + 1)) return true;
                grupos[g].pop();
            }
            return false;
        };
        return colocar(0, 0) ? grupos : null;
    };

    let asignados = armar();
    if (!asignados) {
        // Fallback (no debería pasar con los cupos correctos): sorteo sin restricción.
        asignados = Array.from({ length: NG }, (_, i) => [pots[0][i], pots[1][i], pots[2][i], pots[3][i]]);
    }
    return asignados.map((equipos, i) => ({
        indice: i,
        letra: String.fromCharCode(65 + i), // A..L
        equipos,
    }));
}

// Calendario del mundial: 3 fechas (cada equipo juega 3 partidos a 1 partido).
// Devuelve [fecha1, fecha2, fecha3], cada una con los 24 partidos de esa fecha.
function calendarioGruposMundial(grupos) {
    const fechas = [[], [], []];
    for (const g of grupos) {
        const [a, b, c, d] = g.equipos;
        // Fecha 1: A vs B, C vs D
        // Fecha 2: A vs C, D vs B
        // Fecha 3: A vs D, B vs C
        fechas[0].push({ grupo: g.indice, home: a, away: b });
        fechas[0].push({ grupo: g.indice, home: c, away: d });
        fechas[1].push({ grupo: g.indice, home: a, away: c });
        fechas[1].push({ grupo: g.indice, home: d, away: b });
        fechas[2].push({ grupo: g.indice, home: a, away: d });
        fechas[2].push({ grupo: g.indice, home: b, away: c });
    }
    return fechas;
}

// De la fase de grupos al bracket de 16avos: 12 primeros + 12 segundos + 8 mejores terceros.
function clasificadosAKO(grupos) {
    const primeros = [], segundos = [], terceros = [];
    for (const g of grupos) {
        const tabla = ordenarStandings(g.standings);
        if (tabla[0]) primeros.push({ ...tabla[0], grupo: g.indice, pos: 1 });
        if (tabla[1]) segundos.push({ ...tabla[1], grupo: g.indice, pos: 2 });
        if (tabla[2]) terceros.push({ ...tabla[2], grupo: g.indice, pos: 3 });
    }
    const mejoresTerceros = [...terceros]
        .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf))
        .slice(0, 8);
    // 12 + 12 + 8 = 32 → 16avos de final.
    return [...primeros, ...segundos, ...mejoresTerceros];
}

// ── Resolución de PLAYOFF para el jugador (deterministica, sin turnos) ─────
// Si el país del jugador quedó en repechaje, simulamos el playoff de su confederación con un rng
// determinístico (seed+ciclo+confederación+'playoffJugador'). Resultado: 'clasificado' | 'eliminado'.
function resolverPlayoffJugador(state, seed) {
    const conf = state.confederacion;
    const rng = makeRng(hashSeed(seed, state.ciclo, conf, 'playoffJugador'));
    const tablas = state.grupos.map(g => ordenarStandings(g.standings));
    let playoffSet = [];
    let modo = 'simple'; // 'simple' | 'concacaf'

    if (conf === 'UEFA') {
        const segundos = tablas.map(t => t[1]).filter(Boolean);
        const terceros = tablas.map(t => t[2]).filter(Boolean)
            .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)));
        playoffSet = [...segundos, ...terceros.slice(0, 4)];
    } else if (conf === 'Asia') {
        playoffSet = tablas.map(t => t[2]).filter(Boolean); // 4 terceros
    } else if (conf === 'Africa') {
        const segundos = tablas.map(t => t[1]).filter(Boolean)
            .sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)));
        playoffSet = segundos.slice(0, 4);
    } else if (conf === 'CONCACAF') {
        playoffSet = tablas.map(t => t[1]).filter(Boolean); // 4 segundos
        modo = 'concacaf';
    } else {
        return 'eliminado'; // Conmebol/Oceania no tienen playoff
    }

    // Llevar las entradas de standings a sus objetos seleccion (para usar simBackgroundMatch).
    playoffSet = playoffSet.map(s => SELECCIONES_BY_PAIS[s.pais]).filter(Boolean);
    if (!playoffSet.some(s => s.pais === state.pais)) return 'eliminado';

    if (modo === 'concacaf') {
        // 4 segundos cruzados G1-G3, G2-G4 ida y vuelta → 2 ganadores.
        const cruces = [[playoffSet[0], playoffSet[2]], [playoffSet[1], playoffSet[3]]];
        const ganadores = [];
        for (const [a, b] of cruces) {
            const ida = simBackgroundMatch(a, b, rng);
            const vuelta = simBackgroundMatch(b, a, rng);
            const globalA = ida.gh + vuelta.ga;
            const globalB = ida.ga + vuelta.gh;
            ganadores.push(globalA >= globalB ? a : b);
        }
        return ganadores.some(g => g.pais === state.pais) ? 'clasificado' : 'eliminado';
    }
    // Modo simple: 1 partido cada ronda hasta dejar N ganadores.
    // UEFA: 16 equipos → 4 (2 rondas). Asia/África: 4 → 1 (2 rondas).
    const ganadoresFinales = conf === 'UEFA' ? 4 : 1;
    let vivos = [...playoffSet].sort((a, b) => (a.estrellas || 1) - (b.estrellas || 1));
    while (vivos.length > ganadoresFinales) {
        const next = [];
        for (let i = 0; i < vivos.length / 2; i++) {
            const home = vivos[i];
            const away = vivos[vivos.length - 1 - i];
            const { gh, ga } = simBackgroundMatch(home, away, rng);
            next.push(gh >= ga ? home : away);
        }
        vivos = next;
    }
    return vivos.some(g => g.pais === state.pais) ? 'clasificado' : 'eliminado';
}

// El conjunto de selecciones que van al repechaje en la confederación del jugador (objetos
// seleccion). Mismo criterio que resolverPlayoffJugador, factorizado para reutilizar.
function getPlayoffPool(state) {
    const conf = state.confederacion;
    const tablas = state.grupos.map(g => ordenarStandings(g.standings));
    const byPts = (a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf);
    let pool = [];
    if (conf === 'UEFA') {
        const segundos = tablas.map(t => t[1]).filter(Boolean);
        const terceros = tablas.map(t => t[2]).filter(Boolean).sort(byPts).slice(0, 4);
        pool = [...segundos, ...terceros];
    } else if (conf === 'Asia') {
        pool = tablas.map(t => t[2]).filter(Boolean);
    } else if (conf === 'Africa') {
        pool = tablas.map(t => t[1]).filter(Boolean).sort(byPts).slice(0, 4);
    } else if (conf === 'CONCACAF') {
        pool = tablas.map(t => t[1]).filter(Boolean);
    }
    return pool.map(s => SELECCIONES_BY_PAIS[s.pais]).filter(Boolean);
}

// Arma el grupo de 4 selecciones (para el cuadro de Semifinal+Final) que le toca al jugador
// dentro del pool de repechaje de su confederación. UEFA reparte el pool de 16 (12 segundos + 4
// mejores terceros) en 4 grupos de 4 de forma determinística (mismo patrón de shuffle que el
// resto del motor) y devuelve el grupo que contiene al país del jugador; Asia y África ya tienen
// un pool de exactamente 4 (un solo grupo), se devuelve tal cual.
function armarGrupoDe4(state, seed) {
    const conf = state.confederacion;
    const pool = getPlayoffPool(state);
    if (pool.length < 4) return null; // pool insuficiente — no debería pasar en una partida real
    if (conf !== 'UEFA') return pool.slice(0, 4);
    const rng = makeRng(hashSeed(seed, state.ciclo, conf, 'repechajeGrupos'));
    const mezclado = shuffleDet(pool, rng);
    for (let i = 0; i < mezclado.length; i += 4) {
        const grupo = mezclado.slice(i, i + 4);
        if (grupo.some(s => s.pais === state.pais)) return grupo;
    }
    return mezclado.slice(0, 4); // fallback defensivo, no debería alcanzarse
}

// Construye el CAMINO del jugador en el repechaje, para jugarlo turn-by-turn. Devuelve un objeto
// `playoff` con la lista de partidos (ties) que el jugador debe ganar para clasificar.
// CONCACAF: formato real, 1 sola llave cruzada a ida y vuelta contra 1 rival del pool (sin
// cambios). UEFA/Asia/África: cuadro real de **Semifinal + Final**, cada una a ida y vuelta,
// dentro de un grupo de 4 selecciones (`armarGrupoDe4`). El rival de la Final NO se conoce todavía
// acá — se guardan los otros 2 países del grupo (`otrosParaFinal`) para resolverlo recién si/cuando
// el jugador gana la Semifinal (ver `resolverRivalFinal`, llamado desde careerTurn.js). Ganás por
// global en cada ronda → avanzás; perdés → 'eliminado'. Por qué se juega: terminaste 2º (o mejor
// 3º/2º según confederación) y te toca el repechaje por el último cupo al Mundial.
function construirPlayoffJugador(state, seed) {
    const conf = state.confederacion;
    const rng = makeRng(hashSeed(seed, state.ciclo, conf, 'playoffPath'));

    if (conf === 'CONCACAF') {
        const pool = getPlayoffPool(state);
        const otros = shuffleDet(pool.filter(s => s.pais !== state.pais), rng);
        if (otros.length === 0) return null;
        const rival = otros[0];
        return {
            tipo: conf,
            rival_pais: rival.pais,
            rival_nombre: rival.nombre,
            ties: [
                { ronda: 'Repechaje', rival_pais: rival.pais, rival_nombre: rival.nombre, leg: 'ida' },
                { ronda: 'Repechaje', rival_pais: rival.pais, rival_nombre: rival.nombre, leg: 'vuelta' },
            ],
            idx: 0, agg_favor: 0, agg_contra: 0, fase: 'jugando', eliminados: [],
        };
    }

    const grupo = armarGrupoDe4(state, seed);
    if (!grupo) return null;
    const otros = shuffleDet(grupo.filter(s => s.pais !== state.pais), rng);
    if (otros.length < 3) return null; // un grupo de 4 siempre da 3 rivales
    const semiRival = otros[0];
    const otrosParaFinal = [otros[1].pais, otros[2].pais];
    return {
        tipo: conf,
        rival_pais: semiRival.pais,
        rival_nombre: semiRival.nombre,
        otrosParaFinal,
        ties: [
            { ronda: 'Semifinal', rival_pais: semiRival.pais, rival_nombre: semiRival.nombre, leg: 'ida' },
            { ronda: 'Semifinal', rival_pais: semiRival.pais, rival_nombre: semiRival.nombre, leg: 'vuelta' },
        ],
        idx: 0, agg_favor: 0, agg_contra: 0, fase: 'jugando', eliminados: [],
    };
}

// Simula (recién en el momento en que hace falta, no antes) la OTRA semifinal del grupo de 4 del
// jugador para saber a quién enfrenta en la Final del repechaje. Determinístico con el mismo
// seed/ciclo/confederación que el resto del motor.
function resolverRivalFinal(otrosParaFinal, seed, ciclo, conf) {
    const [paisA, paisB] = otrosParaFinal || [];
    const a = SELECCIONES_BY_PAIS[paisA];
    const b = SELECCIONES_BY_PAIS[paisB];
    if (!a || !b) return a || b || null;
    const rng = makeRng(hashSeed(seed, ciclo, conf, 'otraSemifinalRepechaje', paisA, paisB));
    const ida = simBackgroundMatch(a, b, rng);
    const vuelta = simBackgroundMatch(b, a, rng);
    const globalA = ida.gh + vuelta.ga;
    const globalB = ida.ga + vuelta.gh;
    if (globalA === globalB) return rng() < 0.5 ? a : b; // empate global → azar 50/50
    return globalA > globalB ? a : b;
}

// ── MUNDIAL: init + step ────────────────────────────────────────────────────
//
// Stage 4. Inicia el Mundial cuando termina la temporada del año 4 y el jugador clasificó. Junta
// los 48 clasificados (parallel sim para las otras confederaciones + país del jugador asegurado),
// arma 12 grupos × 4, deja la fase de grupos lista para jugar turn-by-turn.

const KO_ROUNDS = ['16avos', 'octavos', 'cuartos', 'semis', 'final'];

// confJugador = confederación del jugador; paisJugador = país a forzar si CLASIFICÓ (o null);
// estadoClasif = estado real de clasificatorias del jugador (career.seleccion) para derivar SU
// confederación de lo que realmente pasó (no de una simulación paralela aparte).
// incluirJugador = true cuando el jugador CLASIFICÓ (su país debe estar sí o sí). false cuando
// NO clasificó (espectador): su país NO debe aparecer en el Mundial (se reemplaza por otro).
function getClasificados48(confJugador, paisJugador, seed, ciclo, estadoClasif, incluirJugador) {
    const clasificados = [];
    for (const conf of ['UEFA', 'Asia', 'Africa', 'CONCACAF', 'Conmebol', 'Oceania']) {
        const esConfJ = conf === confJugador;
        let c = (esConfJ && estadoClasif && estadoClasif.grupos && estadoClasif.grupos.length)
            ? clasificadosConfDesdeEstado(estadoClasif, seed, ciclo)
            : clasificadosAlMundialConf(conf, seed, ciclo);
        if (esConfJ && paisJugador) {
            const tiene = c.some(s => s && s.pais === paisJugador);
            if (incluirJugador && !tiene) {
                // clasificó pero no quedó en la lista (entró por repechaje): lo forzamos.
                c[c.length - 1] = SELECCIONES_BY_PAIS[paisJugador];
            } else if (!incluirJugador && tiene) {
                // NO clasificó: lo sacamos y lo reemplazamos por la mejor selección que no esté ya.
                const extras = clasificadosAlMundialConf(conf, seed, ciclo);
                const enLista = new Set(c.filter(Boolean).map(s => s.pais));
                const reemplazo = extras.find(s => s && s.pais !== paisJugador && !enLista.has(s.pais))
                    || extras.find(s => s && s.pais !== paisJugador) || null;
                c = c.map(s => (s && s.pais === paisJugador) ? reemplazo : s);
            }
        }
        clasificados.push(...c.filter(Boolean));
    }
    return clasificados.slice(0, 48);
}

function iniciarMundialPlayer(seleccionJugador, paisJugador, seed, ciclo, estadoClasif) {
    const confJ = seleccionJugador?.confederacion || estadoClasif?.confederacion;
    const clasificados = getClasificados48(confJ, paisJugador, seed, ciclo, estadoClasif, true);
    const grupos = sortearMundial(clasificados, seed, ciclo).map(g => ({
        ...g,
        standings: emptyStandings(g.equipos),
    }));
    const fechas = calendarioGruposMundial(grupos);
    const grupoJugadorIdx = grupos.findIndex(g => g.equipos.some(e => e.pais === paisJugador));
    return {
        grupos,
        fechas,                // 3 fechas × 24 partidos
        fechaActual: 0,        // 0..2 = fechas de grupo; pasa a -1 cuando arranca KO
        grupoJugadorIdx,
        fase: grupoJugadorIdx >= 0 ? 'grupos' : 'no_clasif', // si no está, no debería pasar
        ko_ronda: '',          // '16avos'|'octavos'|'cuartos'|'semis'|'final'
        ko_rival_pais: null,   // próximo rival del jugador en KO
        ko_resultados: [],     // [{ronda, pais_rival, gf, gc, ganador_pais}]
        partidosJugador: [],   // [{tipo:'grupo'|'ko', fecha/ronda, rival_pais, gf, gc, goles, asistencias}]
        partidosJugadorJugados: 0,    // partidos que jugó SU SELECCIÓN (con o sin el jugador convocado)
        partidosJugadorConvocado: 0,  // partidos donde el jugador estuvo CONVOCADO (participación real)
        campeon_pais: '',
        subcampeon_pais: '',
        llego_a: '',           // 'grupos'|'16avos'|...|'campeon'
    };
}

// ── Mundial ESPECTADOR (no clasificaste): paso a paso ───────────────────────
// El usuario que no clasificó igual ve el Mundial avanzar fecha por fecha (grupos) y ronda por
// ronda (KO). iniciar() arma el estado; avanzar() juega UN paso (deterministico por seed+ciclo+paso).
const KO_ESPECTADOR = ['16avos', 'octavos', 'cuartos', 'semis', 'final'];

function iniciarMundialEspectador(seed, ciclo, estadoClasif) {
    const confJ = estadoClasif?.confederacion || null;
    // El jugador NO clasificó (por eso es espectador): pasamos su país para EXCLUIRLO del Mundial.
    const clasificados = getClasificados48(confJ, estadoClasif?.pais || null, seed, ciclo, estadoClasif, false);
    const grupos = sortearMundial(clasificados, seed, ciclo).map(g => ({ ...g, standings: emptyStandings(g.equipos) }));
    const fechas = calendarioGruposMundial(grupos);
    return {
        grupos, fechas, fechaActual: 0,
        fase: 'grupos',           // 'grupos' | 'ko' | 'finalizado'
        ko_vivos: [], ko_ronda: '', ko_rondas: [],
        campeon: '', subcampeon: '',
    };
}

// Juega un paso. Devuelve { tipo:'grupos'|'ko', fecha?, ronda?, fin? } para el toast.
function avanzarMundialEspectador(esp, seed, ciclo) {
    if (esp.fase === 'grupos') {
        const f = esp.fechaActual;
        const rng = makeRng(hashSeed(seed, ciclo, 'espFecha', f));
        const byIdx = new Map(esp.grupos.map(g => [g.indice, g]));
        for (const p of (esp.fechas[f] || [])) {
            const g = byIdx.get(p.grupo);
            if (!g) continue;
            const { gh, ga } = simBackgroundMatch(p.home, p.away, rng);
            aplicarResultadoStandings(g.standings, p.home, p.away, gh, ga);
        }
        esp.fechaActual = f + 1;
        let finGrupos = false;
        if (esp.fechaActual >= 3) {
            const rng2 = makeRng(hashSeed(seed, ciclo, 'espKOseed'));
            esp.ko_vivos = shuffleDet(clasificadosAKO(esp.grupos).map(s => s.pais), rng2);
            esp.fase = 'ko';
            esp.ko_ronda = '16avos';
            finGrupos = true;
        }
        return { tipo: 'grupos', fecha: f + 1, fin_grupos: finGrupos };
    }
    if (esp.fase === 'ko') {
        const rondaJugada = esp.ko_ronda;
        const rng = makeRng(hashSeed(seed, ciclo, 'espKO', rondaJugada));
        const vivos = esp.ko_vivos;
        const next = [];
        const partidos = [];
        for (let i = 0; i < vivos.length; i += 2) {
            const a = vivos[i], b = vivos[i + 1];
            if (!b) { next.push(a); continue; }
            const sa = SELECCIONES_BY_PAIS[a], sb = SELECCIONES_BY_PAIS[b];
            let { gh, ga } = simBackgroundMatch(sa, sb, rng);
            let penales = false;
            if (gh === ga) { penales = true; const pa = clamp(0.5 + ((sa.estrellas || 1) - (sb.estrellas || 1)) * 0.05, 0.3, 0.7); if (rng() < pa) gh++; else ga++; }
            const ganador = gh > ga ? a : b;
            partidos.push({ a, b, gh, ga, penales, ganador });
            next.push(ganador);
        }
        esp.ko_rondas.push({ ronda: rondaJugada, partidos });
        if (rondaJugada === 'final') {
            esp.campeon = next[0] || '';
            esp.subcampeon = partidos[0] ? (partidos[0].ganador === partidos[0].a ? partidos[0].b : partidos[0].a) : '';
            esp.fase = 'finalizado';
        } else {
            esp.ko_vivos = next;
            esp.ko_ronda = KO_ESPECTADOR[KO_ESPECTADOR.indexOf(rondaJugada) + 1];
        }
        return { tipo: 'ko', ronda: rondaJugada, fin: esp.fase === 'finalizado' };
    }
    return null;
}

// Devuelve el próximo partido del jugador en la fase de grupos del mundial (o null si ya jugó los 3).
function siguientePartidoGrupoJugador(mundial, paisJugador) {
    for (let f = mundial.fechaActual; f < 3; f++) {
        const fecha = mundial.fechas[f] || [];
        for (const p of fecha) {
            const inv = (p.home?.pais === paisJugador) || (p.away?.pais === paisJugador);
            if (inv && !p.jugado) return { partido: p, fecha: f };
        }
    }
    return null;
}

// Simula los partidos de la fecha actual del grupo del jugador que NO involucran al jugador.
// Determinístico con seed+ciclo+'mundialGrupo'+grupoIdx+fecha.
function simularPartidosBackgroundGrupo(mundial, paisJugador, seed, ciclo, hastaFecha) {
    const grupo = mundial.grupos[mundial.grupoJugadorIdx];
    if (!grupo) return;
    const rng = makeRng(hashSeed(seed, ciclo, 'mundialGrupo', grupo.indice));
    let consumed = 0;
    for (let f = 0; f <= hastaFecha; f++) {
        const fecha = mundial.fechas[f] || [];
        for (const p of fecha) {
            if (p.grupo !== grupo.indice) continue;
            const inv = (p.home?.pais === paisJugador) || (p.away?.pais === paisJugador);
            if (inv) continue; // los del jugador los jugó él
            if (p.jugado) continue; // ya simulado en pasos previos
            // Avanzamos el rng igual para mantener orden determinístico aunque algunos partidos
            // del jugador ya hayan corrido (alteran indirectamente este flujo).
            const { gh, ga } = simBackgroundMatch(p.home, p.away, rng);
            p.gh = gh; p.ga = ga; p.jugado = true;
            aplicarResultadoStandings(grupo.standings, p.home, p.away, gh, ga);
            consumed++;
        }
    }
    return consumed;
}

// Simula, fecha a fecha, los partidos de los OTROS grupos (no el del jugador) hasta la fecha
// indicada (inclusive). Así la tabla de "mejores terceros" se mantiene al día partido a partido,
// igual que el grupo propio, en vez de quedar en cero hasta el final de la fase de grupos.
function simularOtrosGruposHastaFecha(mundial, seed, ciclo, hastaFecha) {
    for (const g of mundial.grupos) {
        if (g.indice === mundial.grupoJugadorIdx) continue;
        const rng = makeRng(hashSeed(seed, ciclo, 'mundialGrupo', g.indice));
        for (let f = 0; f <= hastaFecha; f++) {
            const fecha = mundial.fechas[f] || [];
            for (const p of fecha) {
                if (p.grupo !== g.indice) continue;
                if (p.jugado) continue;
                const { gh, ga } = simBackgroundMatch(p.home, p.away, rng);
                p.gh = gh; p.ga = ga; p.jugado = true;
                aplicarResultadoStandings(g.standings, p.home, p.away, gh, ga);
            }
        }
    }
}

// Simula TODOS los partidos de los grupos OTHER than player's, determinísticamente.
function simularGruposBackgroundMundial(mundial, paisJugador, seed, ciclo) {
    for (const g of mundial.grupos) {
        if (g.indice === mundial.grupoJugadorIdx) continue;
        const rng = makeRng(hashSeed(seed, ciclo, 'mundialGrupoFull', g.indice));
        for (const fecha of mundial.fechas) {
            for (const p of fecha) {
                if (p.grupo !== g.indice) continue;
                if (p.jugado) continue;
                const { gh, ga } = simBackgroundMatch(p.home, p.away, rng);
                p.gh = gh; p.ga = ga; p.jugado = true;
                aplicarResultadoStandings(g.standings, p.home, p.away, gh, ga);
            }
        }
    }
}

// Llamar tras terminar la fase de grupos. Genera el bracket de KO (16avos):
// 12 primeros + 12 segundos + 8 mejores terceros = 32. Emparejados deterministicamente.
function iniciarKO(mundial, seed, ciclo) {
    simularGruposBackgroundMundial(mundial, /*paisJugador*/null, seed, ciclo); // por las dudas
    const clasificados = clasificadosAKO(mundial.grupos);
    // Emparejamiento simple: 1ºA vs 2ºB, 1ºC vs 2ºD, ..., terceros distribuidos.
    // Para no replicar la complejidad real, usamos sorteo determinístico shuffleado.
    const rng = makeRng(hashSeed(seed, ciclo, 'mundialKO16'));
    const shuffled = shuffleDet(clasificados, rng);
    const vivos = shuffled.map(s => s.pais);
    mundial.ko_vivos = vivos;
    mundial.ko_ronda = '16avos';
    mundial.fechaActual = -1;
    mundial.fase = 'ko';
    if (!mundial.ko_rondas) mundial.ko_rondas = []; // bracket completo del torneo (todas las llaves)
}

// Encuentra el cruce KO actual del jugador. Devuelve {rival_pais, idx} o null si quedó eliminado/ya jugó.
function cruceKOJugador(mundial, paisJugador) {
    const vivos = mundial.ko_vivos || [];
    const i = vivos.indexOf(paisJugador);
    if (i < 0) return null;
    // Cada par consecutivo es una llave: 0-1, 2-3, 4-5, ...
    const par = Math.floor(i / 2);
    const rival = vivos[par * 2] === paisJugador ? vivos[par * 2 + 1] : vivos[par * 2];
    return { rival_pais: rival, idx: i, parIdx: par };
}

// Después de que el jugador juega su partido KO, simulamos en background los otros partidos de la
// ronda y armamos los ko_vivos de la próxima ronda.
function avanzarRondaKO(mundial, paisJugador, seed, ciclo) {
    const vivos = mundial.ko_vivos;
    const rondaActual = mundial.ko_ronda;
    const rondaIdx = KO_ROUNDS.indexOf(rondaActual);
    const rng = makeRng(hashSeed(seed, ciclo, 'mundialKORonda', rondaActual));
    const next = [];
    const partidosRonda = []; // [{a,b,gh,ga,penales,ganador}] para dibujar el cuadro completo
    for (let i = 0; i < vivos.length; i += 2) {
        const aPais = vivos[i], bPais = vivos[i + 1];
        if (!aPais || !bPais) { if (aPais) next.push(aPais); continue; }
        // ¿Es la del jugador? Ya se jugó: el resultado está en ko_resultados (gf/gc desde el jugador).
        if (paisJugador && (aPais === paisJugador || bPais === paisJugador)) {
            const ult = mundial.ko_resultados[mundial.ko_resultados.length - 1];
            const ganador = ult?.ganador_pais;
            const gh = aPais === paisJugador ? (ult?.gf ?? 0) : (ult?.gc ?? 0);
            const ga = aPais === paisJugador ? (ult?.gc ?? 0) : (ult?.gf ?? 0);
            partidosRonda.push({ a: aPais, b: bPais, gh, ga, penales: !!ult?.penales, prorroga: !!ult?.prorroga, ganador });
            next.push(ganador);
            continue;
        }
        const sa = SELECCIONES_BY_PAIS[aPais], sb = SELECCIONES_BY_PAIS[bPais];
        let { gh, ga } = simBackgroundMatch(sa, sb, rng);
        // Sin empates en KO: si sigue igual a los 90', prórroga (2x15) y recién si persiste el
        // empate, tanda de penales.
        let penales = false, prorroga = false;
        if (gh === ga) {
            prorroga = true;
            const et = simExtraTimeBackground(sa, sb, rng);
            gh += et.gh; ga += et.ga;
            if (gh === ga) {
                penales = true;
                const ganadorPenales = simPenalesBackground(sa.estrellas || 1, sb.estrellas || 1, rng);
                if (ganadorPenales === 'a') gh++; else ga++;
            }
        }
        const ganador = gh > ga ? aPais : bPais;
        partidosRonda.push({ a: aPais, b: bPais, gh, ga, penales, prorroga, ganador });
        next.push(ganador);
    }
    if (!mundial.ko_rondas) mundial.ko_rondas = [];
    if (!mundial.ko_rondas.some(r => r.ronda === rondaActual)) {
        mundial.ko_rondas.push({ ronda: rondaActual, partidos: partidosRonda });
    }
    mundial.ko_vivos = next;
    if (rondaActual === 'final') {
        // El ganador del último cruce es el campeón; el otro, subcampeón.
        mundial.campeon_pais = next[0] || '';
        const fin = partidosRonda[0];
        if (fin) mundial.subcampeon_pais = fin.ganador === fin.a ? fin.b : fin.a;
        mundial.fase = 'finalizado';
    } else {
        mundial.ko_ronda = KO_ROUNDS[rondaIdx + 1];
    }
}

// Continúa el Mundial UNA ronda después de que el jugador quedó eliminado, para que pueda ver cómo
// termina el torneo (con el cuadro). Simula la ronda completa sin jugador y la registra en ko_rondas.
// Si el jugador fue eliminado en fase de grupos, primero hay que llamar a iniciarKO().
// Devuelve { ronda, fin, campeon }.
function avanzarMundialEliminado(mundial, seed, ciclo) {
    const ronda = mundial.ko_ronda;
    avanzarRondaKO(mundial, null, seed, ciclo);
    return { ronda, fin: mundial.fase === 'finalizado', campeon: mundial.campeon_pais };
}

// ── Calendario de selección DENTRO de la temporada de liga del jugador ─────
//
// Cada año del ciclo (1,2,3 = clasif) el jugador tiene N partidos de selección a jugar. Los
// distribuimos uniformemente en las `totalJornadas` jornadas de su liga, intercalados con la liga
// como "fechas FIFA". Cada turno de selección REEMPLAZA esa jornada de liga (la liga se simula en
// background mientras el jugador está con la selección — pero acá solo decidimos en qué jornadas
// hay turno de selección, el resto la maneja careerTurn).

// Devuelve los partidos del jugador para el año actual del ciclo (los que faltan jugar).
function partidosDelAñoJugador(state, año) {
    if (!state || !state.calendarioJugador) return [];
    return state.calendarioJugador.filter(p => p.año === año && !p.jugado);
}

// Mapa jornadaLiga → índice del partido en state.calendarioJugador para los partidos pendientes
// del año actual. Distribuye uniformemente: si hay 6 partidos en 38 jornadas → jornadas 6,12,...
// Determinístico (no usa rng).
function seleccionScheduleEnSeason(state, año, totalJornadas) {
    if (!state || !state.calendarioJugador) return new Map();
    // Tomamos ÍNDICES de todos los partidos del año (jugados o no) para que la asignación sea
    // estable aunque algunos ya estén jugados (si re-evaluamos a mitad de temporada).
    const indices = state.calendarioJugador.map((p, idx) => ({ p, idx })).filter(x => x.p.año === año);
    if (indices.length === 0 || totalJornadas < 2) return new Map();
    const map = new Map();
    const N = indices.length;
    // Distribución: jornadas 1, ceil(T/(N+1)*1), ..., ceil(T/(N+1)*N). Espacia uniforme dejando
    // jornadas iniciales y finales libres.
    for (let i = 0; i < N; i++) {
        const jornada = Math.max(1, Math.min(totalJornadas, Math.round(((i + 1) * totalJornadas) / (N + 1))));
        // Si colisiona con otra ya asignada (puede pasar con pocos partidos en liga larga), corre +1.
        let j = jornada;
        while (map.has(j) && j < totalJornadas) j++;
        map.set(j, indices[i].idx);
    }
    return map;
}

// ── EXPORTS ─────────────────────────────────────────────────────────────────

export {
    makeRng,
    hashSeed,
    shuffleDet,
    simBackgroundMatch,
    sortearGrupos,
    sortearGruposUEFA,
    sortearGruposAsia,
    sortearGruposAfrica,
    sortearGruposCONCACAF,
    sortearGruposConmebol,
    sortearGruposOceania,
    roundRobinIdaVuelta,
    generarCalendarioGrupo,
    calendarioJugadorEnGrupo,
    emptyStandings,
    aplicarResultadoStandings,
    ordenarStandings,
    iniciarCicloClasif,
    aplicarPartidoJugador,
    refrescarStandingsGrupoJugador,
    procesarFinDeAño,
    determinarClasificacion,
    clasificadosAlMundialConf,
    sortearMundial,
    calendarioGruposMundial,
    clasificadosAKO,
    partidosDelAñoJugador,
    seleccionScheduleEnSeason,
    resolverPlayoffJugador,
    getPlayoffPool,
    construirPlayoffJugador,
    resolverRivalFinal,
    iniciarMundialPlayer,
    iniciarMundialEspectador,
    avanzarMundialEspectador,
    siguientePartidoGrupoJugador,
    simularPartidosBackgroundGrupo,
    simularOtrosGruposHastaFecha,
    simularGruposBackgroundMundial,
    iniciarKO,
    cruceKOJugador,
    avanzarRondaKO,
    avanzarMundialEliminado,
    KO_ROUNDS,
};
